import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Ear, Heart, Sprout } from 'lucide-react';

const Home = () => {
  // Hero image - grounded, natural connection
  const heroImages = [
    'https://customer-assets.emergentagent.com/job_ded95140-8b50-4155-82a1-50fd9b34f147/artifacts/p290wq8k_Rest_barefeet.webp', // Bare feet in grass with blue flowers - grounded, peaceful
  ];
  
  const [activeHeroImage] = useState(0); // Using your custom doula image
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f4f0]">
      {/* Hero Section with Image Background */}
      <section className="relative py-8 px-4 bg-[#f5f4f0]">
        <div 
          className="relative h-[85vh] flex items-center justify-center text-white overflow-hidden rounded-3xl max-w-7xl mx-auto shadow-lg"
        >
          {/* Image Background */}
          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_ded95140-8b50-4155-82a1-50fd9b34f147/artifacts/zjeqj5o0_homepage.webp"
            alt="Flowers in sunlight"
            className="absolute inset-0 w-full h-full object-cover rounded-3xl ken-burns-zoom"
            style={{
              filter: 'brightness(0.7)'
            }}
          />

          {/* Dark overlay - reduced opacity */}
          <div className="absolute inset-0 bg-black opacity-20 rounded-3xl"></div>
          
          <div className="text-center px-4 relative z-10 animate-fadeInUp">
            <p className="text-xs sm:text-sm md:text-base tracking-[0.2em] sm:tracking-[0.3em] uppercase mb-4 animate-fadeIn" style={{ fontFamily: 'Champagne & Limousines, sans-serif', animationDelay: '0.1s', animationFillMode: 'forwards', color: 'rgba(255, 255, 255, 0.95)', opacity: 1, textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}>
              Welcome to Soul Bridge Doula
            </p>
            <h1
              className="text-3xl sm:text-4xl md:text-[60px] mb-6 md:mb-8 leading-tight animate-fadeIn px-2"
              style={{ fontFamily: 'Glory, sans-serif', color: 'white', animationDelay: '0.3s', animationFillMode: 'forwards', opacity: 1, textShadow: '3px 3px 12px rgba(0,0,0,0.9)' }}
            >
              You don't have to navigate motherhood alone.
            </h1>
            <p className="text-base sm:text-lg md:text-[23px] mb-8 max-w-3xl mx-auto animate-fadeIn leading-relaxed px-2" style={{ fontFamily: 'Champagne & Limousines, sans-serif', animationDelay: '0.6s', animationFillMode: 'forwards', opacity: 1, color: 'rgba(255, 255, 255, 0.95)', textShadow: '2px 2px 8px rgba(0,0,0,0.8)' }}>
              A calm, compassionate space for women preparing for birth and adjusting to life after baby. Offering information, practical support and a place to pause, reflect and reconnect with yourself as you navigate one of life's biggest transformations.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-[#815863] text-white px-6 sm:px-8 py-3 rounded-full tracking-wider text-sm sm:text-base hover:bg-[#4d6654] transition-all duration-300 hover:scale-110 hover:shadow-xl animate-fadeIn"
              style={{ fontFamily: 'Champagne & Limousines, sans-serif', animationDelay: '0.9s', animationFillMode: 'forwards', opacity: 1 }}
            >
              BOOK A CONNECTION CALL
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 px-4 bg-[#f5f4f0]">
        <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-lg p-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="overflow-hidden rounded-2xl group">
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_ded95140-8b50-4155-82a1-50fd9b34f147/artifacts/13wd10eq_salada.webp"
              alt="Woman enjoying a healthy meal"
              className="rounded-2xl shadow-lg w-full transform transition-transform duration-700 group-hover:scale-110"
            />
          </div>
          <div className="animate-fadeInRight text-center md:text-left">
            <h2 className="text-3xl md:text-4xl mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#ab6052' }}>
              There is beauty and mess in every motherhood journey
            </h2>
            <p className="text-black mb-4 leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
              Motherhood can feel joyful, overwhelming, tender and uncertain. Sometimes all at once.
            </p>
            <p className="text-black mb-4 leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
              While so much attention is given to welcoming a new baby into the world, becoming a mother deserves just as much care.
            </p>
            <p className="text-black leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
              At Soul Bridge, there is space for all of it, so you don't have to hold it all on your own.
            </p>
          </div>
          </div>
        </div>
      </section>

      {/* How I Help Section */}
      <section className="relative py-20 px-4 bg-gray-50">
        {/* Organic wavy shape at top */}
        <div className="absolute top-0 left-0 w-full overflow-hidden leading-none">
          <svg className="relative block w-full h-12" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0 Q50,30 100,20 T200,20 T300,20 T400,20 T500,20 T600,20 T700,20 T800,20 T900,20 T1000,20 T1100,20 T1200,20 V0 Z" fill="#f5f4f0"></path>
          </svg>
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl text-center mb-6" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#815863' }}>
            Support that meets you where you are.
          </h2>
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-black mb-4 leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
              No two women experience motherhood in the same way. That's why the support I offer is always shaped around you.
            </p>
            <p className="text-black leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
              Whether you're preparing for birth or settling into life with your baby, we'll explore together what feels right for you.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <Ear size={40} strokeWidth={1} className="text-gray-700" />
              </div>
              <h3 className="text-xl font-semibold mb-4 tracking-wider" style={{ fontFamily: 'Cormorant Garamond, serif', color: 'black' }}>A space to be heard</h3>
              <p className="text-black" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
                A place to share your thoughts, process your experience and feel supported without judgement.
              </p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <Heart size={40} strokeWidth={1} className="text-gray-700" />
              </div>
              <h3 className="text-xl font-semibold mb-4 tracking-wider" style={{ fontFamily: 'Cormorant Garamond, serif', color: 'black' }}>Information & practical care</h3>
              <p className="text-black" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
                Clear, evidence-informed information and hands-on support to help you understand your options and make choices that feel right for you.
              </p>
            </div>
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <Sprout size={40} strokeWidth={1} className="text-gray-700" />
              </div>
              <h3 className="text-xl font-semibold mb-4 tracking-wider" style={{ fontFamily: 'Cormorant Garamond, serif', color: 'black' }}>Support that grows with you</h3>
              <p className="text-black" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
                Personalised support through pregnancy and the early weeks and months of motherhood, shaped around where you are and what you need.
              </p>
            </div>
          </div>
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-block bg-[#815863] text-white px-8 py-3 rounded-full tracking-wider text-sm hover:bg-[#4d6654] transition-all duration-300 hover:scale-105"
              style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}
            >
              EXPLORE HOW I CAN SUPPORT YOU
            </Link>
          </div>
        </div>
      </section>

      {/* Meet Cris Preview Section */}
      <section className="py-20 px-4 bg-gray-50 relative overflow-hidden">
        {/* Animated Background - 15% faster */}
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1714636608872-048fc9231892?w=1600&q=80)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            animation: 'slowPan 25.5s ease-in-out infinite alternate'
          }}
        />
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center relative z-10">
          <div className="overflow-hidden rounded-3xl mx-auto group shadow-2xl max-w-md">
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_ded95140-8b50-4155-82a1-50fd9b34f147/artifacts/y9htrx0e_cris%20verde.JPG"
              alt="Cristiane - Doula"
              className="rounded-3xl w-full mx-auto transition-transform duration-700 group-hover:scale-105"
              style={{ 
                aspectRatio: '3/4', 
                objectFit: 'cover', 
                objectPosition: '45% center',
                transform: 'scale(0.85)'
              }}
            />
          </div>
          <div className="animate-fadeInLeft text-center md:text-left">
            <h2
              className="text-3xl md:text-4xl mb-6"
              style={{ fontFamily: 'Cormorant Garamond, serif', color: '#ab6052' }}
            >
              For the woman you're becoming
            </h2>
            <p className="text-black mb-4 leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
              Most people think the biggest change they'll experience is the arrival of their baby. But there's a quieter, deeper transformation happening too: the woman you are becoming.
            </p>
            <p className="text-black mb-4 leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
              Motherhood can change how you see yourself, your relationships, your priorities and the world around you. It's a lot to take in. And sometimes, becoming a mother can also mean grieving parts of the woman you once were.
            </p>
            <p className="text-black mb-4 leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
              That's why Soul Bridge was created.
            </p>
            <p className="text-black mb-6 leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}>
              A bridge you can cross at your own pace, in your own way, connecting who you were with who you are becoming. A space to pause, explore and make sense of what is changing, with somebody beside you as you find your way through this new season.
            </p>
          </div>
        </div>
      </section>

      {/* Beauty and Mess Section - Text Only with Soft Rose Box */}
      <section className="py-16 px-4 bg-[#f5f4f0]">
        <div className="max-w-6xl mx-auto">
          <div className="bg-[#f5e8e8] rounded-3xl shadow-lg p-12 md:p-16">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-2xl md:text-3xl mb-8 leading-relaxed" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#815863' }}>
                Maybe motherhood was never meant to be perfect.
              </p>
              <p className="text-lg md:text-xl mb-8 leading-relaxed" style={{ fontFamily: 'Champagne & Limousines, sans-serif', color: '#4d6654' }}>
                What women need is to feel safe. To feel seen. To be cared for in a way that allows them to trust themselves again, and find their own way as they become mothers.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-[#815863] text-white px-8 py-3 rounded-full tracking-wider text-sm hover:bg-[#4d6654] transition-all duration-300 hover:scale-110 hover:shadow-xl"
                style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}
              >
                BOOK A CONNECTION CALL
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <div className="py-8 px-4 bg-[#f5f4f0] text-center">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center gap-2 text-[#815863] hover:text-[#4d6654] transition-colors duration-300"
          style={{ fontFamily: 'Champagne & Limousines, sans-serif' }}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          Back to Top
        </button>
      </div>
    </div>
  );
};

export default Home;